import { useMemo } from "react";
import {
  APPLICATION_STATUS_FUNNEL_ORDER,
  getApplicationStatusColor,
  isApplicationStatus,
  type ApplicationPipelineStage,
  type PipelineTransition
} from "api/crm-analytics";
import { PipelineCard } from "./PipelineCard";

const FALLBACK_STAGE_COLOR = "#9CA3AF";

const APPLICATION_PIPELINE_HELP_TEXT =
  "The number in each status represents how many Applications moved into that status within the selected period. For example, if Submitted shows 13, it means 13 Applications moved into the Submitted stage in the selected period.";

const filterAndSortBuckets = (
  data: ApplicationPipelineStage[] | undefined
): ApplicationPipelineStage[] | undefined => {
  if (!data) return data;
  const byStatus = new Map<string, ApplicationPipelineStage>();
  data.forEach((stage) => {
    if (!isApplicationStatus(stage.stage_id)) return;
    byStatus.set(stage.stage_id, stage);
  });
  return APPLICATION_STATUS_FUNNEL_ORDER.map((id) => byStatus.get(id)).filter(
    (s): s is ApplicationPipelineStage => s !== undefined
  );
};

const getStageColor = (stage: ApplicationPipelineStage) =>
  getApplicationStatusColor(stage.stage_id) ?? FALLBACK_STAGE_COLOR;

const getTransitionColor = (transition: PipelineTransition) =>
  (transition.stage_id !== null &&
    getApplicationStatusColor(transition.stage_id)) ||
  FALLBACK_STAGE_COLOR;

export const ApplicationPipelineCard = ({
  data,
  isLoading,
  dateLabel,
  onStageClick
}: {
  data: ApplicationPipelineStage[] | undefined;
  isLoading: boolean;
  dateLabel: string;
  onStageClick?: (stage: ApplicationPipelineStage) => void;
}) => {
  const visibleData = useMemo(() => filterAndSortBuckets(data), [data]);

  return (
    <PipelineCard
      data={visibleData}
      isLoading={isLoading}
      title="Application pipeline"
      subtitle={`Application stage progression – ${dateLabel}`}
      helpText={APPLICATION_PIPELINE_HELP_TEXT}
      variant="single"
      getStageColor={getStageColor}
      getTransitionColor={getTransitionColor}
      onStageClick={onStageClick}
    />
  );
};
